import React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, type SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { bankCardMetadata } from "@features/Budget/budgetConstants";
import { useBudgetFunctionalityDisabledError } from "@features/Budget/hooks/useBudgetFunctionalityDisabledError";
import { Button } from "@components/Button";
import { BinIcon, LoaderIcon, PencilIcon } from "@components/icons";
import { FormInput } from "@components/inputs/form/FormInput/FormInput";
import { FormSelect } from "@components/inputs/form/FormSelect/FormSelect";
import { Select } from "@components/inputs/Select/Select";
import { Modal } from "@components/Modal/Modal";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useModal } from "@hooks/useModal";
import { type IUseModalProps } from "@store/modal/modal";
import {
  useEditExpensePlanMutation,
  useRemoveExpensePlanMutation,
} from "@services/budget/mutations";
import { useGetExpensePlanQuery } from "@services/budget/queries";
import {
  type IExpensePlan,
  type IExpensePlanBankAccountsResponse,
} from "@services/budget/types";
import { parseDecimalNumberInputValue } from "@utils/units";
import {
  useEditExpensePlanSchema,
  type TEditExpensePlanSchema,
} from "./useEditExpensePlanSchema";

interface IEditExpensePlanModalProps extends IUseModalProps {
  expensePlanId: number;
  expensePlan: IExpensePlan;
  expensePlanBankAccounts: IExpensePlanBankAccountsResponse;
}

export const EditExpensePlanModal: React.FC<IEditExpensePlanModalProps> = (
  props,
) => {
  const { expensePlanId, expensePlan, expensePlanBankAccounts, ...rest } =
    props;

  const modal = useModal(rest);
  const { t } = useTranslation();

  const { data: extendedExpensePlanData } = useGetExpensePlanQuery({
    expensePlanId,
  });

  const { mutateAsync, isPending } = useEditExpensePlanMutation();
  const { mutateAsync: removeExpense, isPending: isRemovePending } =
    useRemoveExpensePlanMutation();

  const schema = useEditExpensePlanSchema();

  const { control, setValue, getValues, handleSubmit, setError, watch } =
    useForm<TEditExpensePlanSchema>({
      resolver: zodResolver(schema),
      defaultValues: {
        planName: expensePlan.planName,
        planAmount: expensePlan.planAmount,
        bankAccountId: expensePlan.bankAccount?.bankAccountId ?? 0,
      },
    });

  const { setApiErrors, toastApiError } = useApiErrorUtils(setError);
  const { handleBudgetError } = useBudgetFunctionalityDisabledError();

  const onSubmit: SubmitHandler<TEditExpensePlanSchema> = async (data) => {
    const response = await mutateAsync({
      expensePlanId,
      planAmount: parseDecimalNumberInputValue(data.planAmount.toString()),
      planName: data.planName,
      bankAccountId: data.bankAccountId === 0 ? undefined : data.bankAccountId,
    }).catch(handleBudgetError(setApiErrors));

    if (response) {
      toast.success(t("budget.expenses.editExpensePlanModal.success"));
      modal.close();
    }
  };

  const bankAccountId = watch("bankAccountId");

  const currentBankIcon =
    bankCardMetadata[
      expensePlanBankAccounts.bankAccounts.find(
        (bank) => bank.bankAccountId === bankAccountId,
      )?.bankName ?? "Default"
    ]?.iconSmall;

  const bankSelectOptions = [
    {
      label: t("budget.expenses.editExpensePlanModal.allBankAccounts"),
      value: 0,
      icon: undefined,
    },
    ...expensePlanBankAccounts.bankAccounts.map((bank) => ({
      label: bank.iban,
      value: bank.bankAccountId,
      icon: bankCardMetadata[bank.bankName].iconSmall,
    })),
  ];

  const onRemove = async () => {
    const response = await removeExpense({
      expensePlanId: expensePlanId,
    }).catch(handleBudgetError(toastApiError));

    if (response) {
      toast.success(t("budget.expenses.editExpensePlanModal.remove.success"));
      modal.close();
    }
  };

  return (
    <Modal
      modal={modal}
      title={t("budget.expenses.editExpensePlanModal.title")}
      badgeProps={{ icon: PencilIcon, color: "green" }}
      actionButtons={
        <React.Fragment>
          <Button className="w-full" variant="secondary" onClick={modal.close}>
            {t("common.form.buttons.cancel")}
          </Button>
          <Button
            className="w-full"
            onClick={handleSubmit(onSubmit)}
            isLoading={isPending}
          >
            {t("common.form.buttons.submit")}
          </Button>
        </React.Fragment>
      }
    >
      <div className="flex flex-col gap-4">
        <Select
          label={t("budget.expenses.editExpensePlanModal.transactionCategory")}
          options={[
            {
              label:
                extendedExpensePlanData?.customerCategoryName ??
                t(`budget.category.${extendedExpensePlanData?.categorySlug}`),
              value: extendedExpensePlanData?.categorySlug ?? "",
            },
          ]}
          value={extendedExpensePlanData?.categorySlug}
          disabled
        />
        <FormInput
          label={t("budget.expenses.editExpensePlanModal.planName")}
          control={control}
          name="planName"
        />
        <FormInput
          label={t("budget.expenses.editExpensePlanModal.planAmmount")}
          control={control}
          name="planAmount"
          onBlur={() =>
            setValue(
              "planAmount",
              parseDecimalNumberInputValue(getValues("planAmount").toString()),
            )
          }
        />
        <FormSelect
          label={t("budget.expenses.editExpensePlanModal.bankAccount")}
          name="bankAccountId"
          control={control}
          icon={currentBankIcon}
          options={bankSelectOptions}
        />
        <Select
          label={t("budget.expenses.editExpensePlanModal.interval")}
          options={[
            {
              label: t(
                `budget.interval.${extendedExpensePlanData?.expensePlanIntervalId}`,
              ),
              value: extendedExpensePlanData?.expensePlanIntervalId ?? "",
            },
          ]}
          value={extendedExpensePlanData?.expensePlanIntervalId}
          disabled
        />
        <button
          className="flex w-fit items-center gap-2 pt-2 text-sm font-semibold text-red-dark"
          disabled={isRemovePending}
          onClick={onRemove}
        >
          <BinIcon className="size-5 stroke-2" />
          {t("budget.expenses.editExpensePlanModal.remove.button")}
          {isRemovePending && (
            <LoaderIcon className="h-5 w-5 animate-spin text-grey-dark" />
          )}
        </button>
      </div>
    </Modal>
  );
};
